/**
 * thumbnailService.ts — Generischer Thumbnail-Capture + Persist Service
 *
 * Erfasst Medium (320px) + High (640px) Thumbnails aus dem Potree WebGL-Canvas
 * und speichert sie per Store-Update (→ API PATCH) fuer Scenes, Audits und
 * Annotations. Dispatcht CustomEvents fuer Live-Updates in Whiteboard-Cards.
 */

import { captureViewerThumbnailMediumHigh } from './captureViewerThumbnail'
import { captureViewerThumbnailPair } from './captureViewerThumbnail'
import { useSceneStore } from '../store/scenes'
import { useAnnotationStore } from '../store/annotations'

export interface ThumbnailMediumHigh {
  medium: string
  high: string
}

// ── Scene ──────────────────────────────────────────────────────────────────────

/**
 * Erfasst Thumbnails und speichert sie in der Scene (Store → API).
 * Erzeugt zusaetzlich small (88px) fuer Listen + medium + high fuer Whiteboard.
 */
export function captureAndStoreSceneThumbnail(
  sceneId: string,
): ThumbnailMediumHigh | null {
  // Small+Medium fuer bestehende Listenansicht
  const pair = captureViewerThumbnailPair()
  // Medium+High fuer Whiteboard-Cards
  const mh = captureViewerThumbnailMediumHigh()

  if (!pair && !mh) {
    console.warn('[thumbnail] Capture fehlgeschlagen fuer Scene', sceneId)
    return null
  }

  const thumbnailSmall = pair?.small
  const thumbnailMedium = mh?.medium || pair?.medium
  const thumbnailHigh = mh?.high

  // Im Store aktualisieren (loest async PATCH /api/v1/scenes/{id} aus)
  useSceneStore.getState().updateScene(sceneId, {
    ...(thumbnailSmall && { thumbnailSmall }),
    ...(thumbnailMedium && { thumbnailMedium }),
    ...(thumbnailHigh && { thumbnailHigh }),
  })

  // Event fuer Whiteboard-Cards (SceneCardBody lauscht darauf)
  window.dispatchEvent(
    new CustomEvent('orto:scene-thumbnail-updated', {
      detail: {
        sceneId,
        thumbnailSmall,
        thumbnailMedium,
        thumbnailHigh,
      },
    }),
  )

  console.log('[thumbnail] Scene-Thumbnails gespeichert', sceneId,
    `medium=${thumbnailMedium?.length ?? 0}B high=${thumbnailHigh?.length ?? 0}B`)

  return thumbnailMedium && thumbnailHigh ? { medium: thumbnailMedium, high: thumbnailHigh } : null
}

// ── Annotation ─────────────────────────────────────────────────────────────────

/**
 * Erfasst Medium+High Thumbnails und speichert sie in der Annotation (Store → API).
 */
export function captureAndStoreAnnotationThumbnail(
  annotationId: string,
): ThumbnailMediumHigh | null {
  const mh = captureViewerThumbnailMediumHigh()
  if (!mh) {
    console.warn('[thumbnail] Capture fehlgeschlagen fuer Annotation', annotationId)
    return null
  }

  // Im Store aktualisieren (loest async PATCH /api/v1/annotations/{id} aus)
  useAnnotationStore.getState().updateAnnotation(annotationId, {
    thumbnailMedium: mh.medium,
    thumbnailHigh: mh.high,
  })

  // Event fuer Whiteboard-Cards
  window.dispatchEvent(
    new CustomEvent('orto:annotation-thumbnail-updated', {
      detail: {
        annotationId,
        thumbnailMedium: mh.medium,
        thumbnailHigh: mh.high,
      },
    }),
  )

  console.log('[thumbnail] Annotation-Thumbnails gespeichert', annotationId,
    `medium=${mh.medium.length}B high=${mh.high.length}B`)

  return mh
}

// ── Audit ──────────────────────────────────────────────────────────────────────

import { apiFetch, projectUrl } from './apiBase'

/**
 * Erfasst Medium+High Thumbnails fuer einen Audit.
 * Audits werden ueber window._auditObjects verwaltet; Persist erfolgt
 * beim naechsten Bulk-Save. Zusaetzlich: sofort PATCH an die API.
 */
export function captureAndStoreAuditThumbnail(
  auditId: string,
): ThumbnailMediumHigh | null {
  const mh = captureViewerThumbnailMediumHigh()
  if (!mh) {
    console.warn('[thumbnail] Capture fehlgeschlagen fuer Audit', auditId)
    return null
  }

  // In-Memory-Objekt aktualisieren
  const audits = (window as any)._auditObjects as any[] | undefined
  if (audits) {
    const obj = audits.find((a: any) => a.id === auditId)
    if (obj) {
      obj.thumbnailMedium = mh.medium
      obj.thumbnailHigh = mh.high
      // Legacy-Feld fuer Abwaertskompatibilitaet
      obj.thumbnail = mh.medium
    }
  }

  // config.json aktualisieren (damit Thumbnails beim naechsten Laden da sind)
  if (typeof (window as any).saveAuditsToConfig === 'function') {
    try { (window as any).saveAuditsToConfig() } catch { /* noop */ }
  }

  // Sofort per API persistieren
  apiFetch(projectUrl(`/audits/${auditId}`), {
    method: 'PATCH',
    body: JSON.stringify({
      thumbnailMedium: mh.medium,
      thumbnailHigh: mh.high,
    }),
  }).catch(err => console.warn('[thumbnail] Audit-PATCH fehlgeschlagen:', err))

  // Event fuer Whiteboard-Cards
  window.dispatchEvent(
    new CustomEvent('orto:audit-thumbnail-updated', {
      detail: {
        auditId,
        thumbnailMedium: mh.medium,
        thumbnailHigh: mh.high,
        // Legacy
        thumbnail: mh.medium,
      },
    }),
  )

  console.log('[thumbnail] Audit-Thumbnails gespeichert', auditId,
    `medium=${mh.medium.length}B high=${mh.high.length}B`)

  return mh
}
